import {
  type DateTimeFormatOptions,
  type MessageKeys,
  type NestedKeyOf,
  type AppConfig,
  createFormatter,
  useFormatter,
  useTranslations,
  Locale,
} from 'next-intl'
import { useLocalisation, formatRichMessageForKey, renderHtmlTagMap } from '~/context/localisation'

/**
 * @deprecated use the package next-intl directly when retrieving translations.
 * see MICROCOPY.md for migration steps
 */
export type LegacyMessageDescriptor = {
  id: string
  description?: string | object
  defaultMessage: string
}

type FormatterShape = ReturnType<typeof createFormatter>

type IntlShape = {
  /**
   * @deprecated please use the defaultMessage json with useTranslations from package 'next-intl'
   */
  formatMessage(descriptor: LegacyMessageDescriptor, values?: Record<string, any>): string // | React.ReactNode
  /**
   * @deprecated please use the useFormatter from package 'next-intl'
   */
  formatNumber: FormatterShape['number']
  /**
   * @deprecated please use the useFormatter from package 'next-intl'
   */
  formatDate(date: number | string | Date, opt?: DateTimeFormatOptions): string
  /**
   * @deprecated please use the useFormatter from package 'next-intl'
   */
  formatTime(date: number | string | Date, opt?: DateTimeFormatOptions): string
  /**
   * @deprecated source of truth exists in localisation context
   */
  locale: string
}

// Formats the default message using the html tag map and any injected variables
// used to cover any messages missing from the json files
export const formatLegacyMessage = (
  descriptor: LegacyMessageDescriptor,
  values?: Record<string, any>,
  locale: Locale = 'en-US'
) => {
  const key = descriptor.id
  const messages = {
    [key]: descriptor.defaultMessage,
  } as const

  return formatRichMessageForKey(key, messages, values, locale)
}

/**
 * Legacy hook for retrieving translations
 * @deprecated use next-intl directly to retrieve translations by adding to ->
 * /i18n/defaultMessages/messages.en.json.
 * More docs here https://next-intl.dev/ and in MICROCOPY.md
 */
const useIntl = (): IntlShape => {
  const t = useTranslations()
  const f = useFormatter()
  const { locale } = useLocalisation()

  const formatMessage: IntlShape['formatMessage'] = (descriptor, values) => {
    const key = descriptor.id as unknown as MessageKeys<
      AppConfig['Messages'],
      NestedKeyOf<AppConfig['Messages']>
    >
    const richTextValue = t.rich(key, { ...values, ...renderHtmlTagMap }, {}) as string

    // No default message found in json, fallback to legacy defined value
    if (richTextValue === descriptor.id) {
      return formatLegacyMessage(descriptor, values, locale)
    }

    return richTextValue
  }
  const formatNumber: IntlShape['formatNumber'] = f.number
  const formatDate: IntlShape['formatDate'] = (value, opt) => f.dateTime(new Date(value), opt)
  const formatTime: IntlShape['formatTime'] = (value, opt) => f.dateTime(new Date(value), opt)

  return { formatMessage, formatTime, formatDate, formatNumber, locale }
}

export default useIntl
